import { driver, type DriveStep } from 'driver.js';
import { api } from '../api/client';
import 'driver.js/dist/driver.css';
import './tour.css';

/**
 * Interaktivní průvodce. Pouští se sám při první návštěvě a z hlavičky kdykoli znovu.
 * <p>
 * Kroky míří na prvky s `data-tour="…"`. Krok, jehož prvek na stránce zrovna není (třeba
 * prázdný seznam), se přeskočí — průvodce nesmí ukazovat do prázdna.
 * <p>
 * „Už jsem viděl" se drží v prohlížeči. Je to pohodlí, ne data: když se to ztratí,
 * člověk uvidí průvodce ještě jednou a nic se nestane.
 */
export type TourName = 'home' | 'detail';

type Step = { target: string; title: string; text: string };

const tours: Record<TourName, Step[]> = {
  home: [
    { target: 'my-turn', title: 'Na tahu jste vy', text: 'Tady je všechno, co teď čeká na vás. Modrý text říká, co udělat — klikněte a jste na místě.' },
    { target: 'questions', title: 'Dotazy a úkoly', text: 'Kolegové se vás na něco ptají nebo pro vás mají úkol. Odpovídá se přímo v zakázce.' },
    { target: 'watching', title: 'Čekáte na ostatní', text: 'Zakázky, které jste založili nebo na nich děláte — a na koho se u nich zrovna čeká.' },
    { target: 'new-contract', title: 'Nová zakázka', text: 'Stačí zákazník, název a co se bude stavět. Technické detaily doplní vedení později.' },
    { target: 'nav-contracts', title: 'Všechny zakázky', text: 'Přehled všech rozpracovaných zakázek. Hotové a zrušené jsou v archivu.' },
    { target: 'nav-settings', title: 'Než začnete', text: 'V Nastavení si zaškrtněte, co děláte (třeba vedení nebo výroba). Jinak vám nebude chodit práce určená vaší roli.' },
  ],
  detail: [
    { target: 'next-step', title: 'Kdo je na tahu', text: 'Nahoře je vždycky vidět, na koho se čeká. Když na vás, je tu tlačítko s dalším krokem — nic jiného hledat nemusíte.' },
    { target: 'add-question', title: 'Něco není jasné?', text: 'Zeptejte se nebo zadejte úkol. Zaškrtnutý dotaz zakázku zastaví, dokud ho někdo nevyřídí.' },
    { target: 'more-actions', title: 'Další akce', text: 'Vrátit, zrušit nebo smazat omylem založenou zakázku. Dělá se to zřídka, proto je to schované.' },
  ],
};

let active: ReturnType<typeof driver> | null = null;
let welcomeOpen = false;

/** Během uvítání se prohlídka nespouští — přes dialog by stejně nebyla vidět. */
export function setWelcomeOpen(open: boolean) {
  welcomeOpen = open;
}

/** Zavře rozdělanou prohlídku — volá se při přechodu na jinou stránku, kde by ukazovala do prázdna. */
export function stopTour() {
  active?.destroy();
  active = null;
}

/**
 * Pamět „tenhle průvodce už se ukázal“ je na serveru u účtu (nastavení `tour.seen.*`).
 * Dřív byla v prohlížeči — a ztrácela se s jiným počítačem, mobilem nebo vymazáním dat,
 * takže průvodce lidi zasypával pořád znovu (zpětná vazba z porady 9. 10. 2026).
 */
const settingKey = (name: TourName) => `tour.seen.${name}`;
const legacyKey = (name: TourName, memberId: number) => `contracts.tour.${name}.${memberId}`;

let seen: Promise<Set<string>> | null = null;

function loadSeen(memberId: number): Promise<Set<string>> {
  seen ??= api.GET('/api/v1/me/settings').then(({ data }) => {
    // Nenacteno = radsi neotravovat, nez ukazat znovu nekomu, kdo uz videl.
    if (!data) return new Set(Object.keys(tours).map((n) => settingKey(n as TourName)));

    const set = new Set(data.filter((s) => s.isHidden && s.value === true).map((s) => s.key));

    // Kdo pruvodce videl jeste v dobe pameti v prohlizeci, ten ho znovu nedostane.
    for (const name of Object.keys(tours) as TourName[]) {
      if (!set.has(settingKey(name)) && readLegacy(name, memberId)) markSeen(name, set);
    }

    return set;
  });

  return seen;
}

function readLegacy(name: TourName, memberId: number) {
  try {
    return window.localStorage.getItem(legacyKey(name, memberId)) === '1';
  } catch {
    return false;
  }
}

function markSeen(name: TourName, set?: Set<string>) {
  set?.add(settingKey(name));
  void seen?.then((s) => s.add(settingKey(name)));
  void api.PUT('/api/v1/me/settings', { body: { [settingKey(name)]: true } });
}

export function startTour(name: TourName) {
  const steps: DriveStep[] = tours[name]
    .map((step) => ({ step, element: document.querySelector(`[data-tour="${step.target}"]`) }))
    .filter((s): s is { step: Step; element: Element } => s.element !== null)
    .map(({ step, element }) => ({ element, popover: { title: step.title, description: step.text } }));

  if (steps.length === 0) return;

  const tour = driver({
    steps,
    showProgress: steps.length > 1,
    progressText: '{{current}} z {{total}}',
    nextBtnText: 'Další',
    prevBtnText: 'Zpět',
    doneBtnText: 'Rozumím',
    popoverClass: 'st-tour',
    stagePadding: 6,
    stageRadius: 6,
    // Zavreni krizkem nebo klikem vedle = taky "videl jsem". Kdo chce znovu, ma tlacitko v hlavicce.
    onDestroyed: () => markSeen(name),
  });

  stopTour();
  active = tour;
  tour.drive();
}

/** Spustí průvodce, jestli ho člověk ještě neviděl. Volá se, až je obsah stránky načtený. */
export function startTourOnce(name: TourName, memberId: number) {
  if (welcomeOpen) return;

  // Az po vykresleni - kroky hledaji prvky v DOM. Kdyz clovek mezitim odesel jinam,
  // prohlidka by ukazovala na cizi stranku (presne to se stalo po "Vyzkoušet").
  const path = window.location.pathname;

  void loadSeen(memberId).then((set) => {
    if (set.has(settingKey(name))) return;
    window.setTimeout(() => {
      if (!welcomeOpen && window.location.pathname === path) startTour(name);
    }, 300);
  });
}
